/**
 * What a person at this computer plays with when several people sit at it and do not share:
 * the mouse and keyboard (one seat at most, since there is one cursor), or one particular
 * gamepad. A pad is named by the slot the browser gave it and by its id, so two identical pads
 * stay apart and a pad that went away is noticed rather than silently replaced by another.
 */
export type SeatDevice = 'mouse' | { pad: number; id: string };

export function sameDevice(a: SeatDevice | undefined, b: SeatDevice | undefined): boolean {
  if (a === undefined || b === undefined) return false;
  if (a === 'mouse' || b === 'mouse') return a === b;
  return a.pad === b.pad && a.id === b.id;
}

/** Whether a connected pad is the one a seat chose. */
export function padMatches(
  pad: Gamepad | null | undefined,
  device: SeatDevice | undefined,
): boolean {
  return (
    !!pad?.connected &&
    device !== undefined &&
    device !== 'mouse' &&
    pad.index === device.pad &&
    pad.id === device.id
  );
}

/**
 * The pads a seat plays with now: its own, or, while its slot is empty, one pad of the same make
 * on a slot no other seat has. A controller whose battery died or whose Bluetooth dropped comes
 * back on another slot, and was never matched again (client-input:quality#6). Two pads of one
 * make are still told apart by their slots while both are there.
 */
export function seatPads(
  pads: readonly (Gamepad | null)[],
  device: SeatDevice | undefined,
  others: readonly (SeatDevice | undefined)[] = [],
): Gamepad[] {
  if (device === undefined || device === 'mouse') return [];
  const own = pads.filter((pad): pad is Gamepad => padMatches(pad, device));
  if (own.length > 0) return own;
  const taken = new Set(
    others.flatMap((other) => (other === undefined || other === 'mouse' ? [] : [other.pad])),
  );
  const back = pads.find(
    (pad) => !!pad?.connected && pad.id === device.id && !taken.has(pad.index),
  );
  return back ? [back] : [];
}

/** A connected controller as the setup screen lists it. */
export interface PadListing {
  index: number;
  id: string;
  /** A button is down right now, so a player can tell which listed pad is in their hands. */
  pressed: boolean;
}

export function listGamepads(
  pads: () => readonly (Gamepad | null)[] = () =>
    typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function'
      ? navigator.getGamepads()
      : [],
): PadListing[] {
  const out: PadListing[] = [];
  for (const pad of pads()) {
    if (!pad?.connected) continue;
    out.push({
      index: pad.index,
      id: pad.id,
      pressed: pad.buttons.some((b) => b.pressed || b.value > 0.3),
    });
  }
  return out;
}

/**
 * A pad's name as a person knows it: "Pad 1 · 8BitDo Pro 2", without the vendor and product
 * codes and the "(STANDARD GAMEPAD)" note the browser appends.
 */
export function padLabel(index: number, id: string): string {
  const name = id
    .replace(/\([^)]*(?:STANDARD GAMEPAD|Vendor|Product)[^)]*\)/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  return `Pad ${index + 1}${name ? ` · ${name}` : ''}`;
}

export function describeDevice(device: SeatDevice): string {
  return device === 'mouse' ? 'Mouse & keyboard' : padLabel(device.pad, device.id);
}
